import React, { useEffect, useState } from 'react'
import Module from '../../../../layout/Module'
import { Box, Step, StepLabel, Stepper } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import useDevelopmentalRubrics from '../../../../../hooks/developmental-rubrics/useDevelopmentalRubrics'
import { Alert } from '@mui/material'
import AlertSaveButton from '../../../../button/AlertSaveButton'
import isEmpty from 'lodash/isEmpty'
import { toggleAlertModule } from '../../../../../store/actions/alert.actions'
import {
  updateDevelopmentalRubricFormField,
  updateDevelopmentalRubricFormError,
  updateDevelopmentalRubricPreview
} from '../../../../../store/actions/developmental-rubrics.actions'
import FormCurriculumElements from '../FormCurriculumElements/FormCurriculumElements'
import FormBasicInformation from '../FormBasicInformation/FormBasicInformation'
import FormSubject from '../FormSubject/FormSubject'
import FormLearningContinuum from '../FormLearningContinuum/FormLearningContinuum'
import FormGranularSteps from '../FormGranularSteps/FormGranularSteps'
import IconTooltipButton from '../../../../button/IconTooltipButton'
import CardsInformation from '../CardsInformation/CardsInformation'
import { isBefore } from 'date-fns'

const Create = () => {
  const { createDevelopmentalRubric, validateFormDevelopmentalRubrics } = useDevelopmentalRubrics()
  const dispatch = useDispatch()
  const steps = ['Basic Information', 'Learning continuum', 'Granular Steps']
  const formData = useSelector((state) => state?.dr?.form || {})
  const today = useSelector((state) => state?.date?.date || new Date())

  const alertSave = useSelector((state) => state?.alert?.save || {})

  const permission = useSelector((state) => state?.session?.permissions?.dr || {})
  const { curriculumId, step } = useParams()
  const [error, setError] = useState(null)
  const navigate = useNavigate()
  const basepath = `/dashboard/developmental-rubrics/${curriculumId}`

  const [loading, toggleLoading] = useState(false)

  const onMinMaxDate = (key, value) => {
    if (isBefore(today, value)) {
      dispatch(updateDevelopmentalRubricFormField(key, value))
    } else {
      dispatch(updateDevelopmentalRubricFormField(key, today))
    }
  }

  const onCancel = () => {
    navigate(`${basepath}`)
    dispatch(toggleAlertModule(null))
  }
  const onRedirect = (step) => {
    navigate(`${basepath}/create/step/${step}`)
  }
  const onChangeAlert = (key, value) => {
    dispatch(updateDevelopmentalRubricFormField(key, value))
    if (key === 'startDate') {
      onMinMaxDate('minEndDate', value)
    }

    if (key === 'endDate') {
      dispatch(updateDevelopmentalRubricFormField('maxStartDate', value))
    }

    if (!alertSave.mode || !alertSave.onSave) {
      dispatch(toggleAlertModule(false, 'Developmental rubrics', 'create', onSave, alertSave.onDontSave))
    }
  }

  const onNext = () => {
    setError(null)
    const errors = validateFormDevelopmentalRubrics(formData, `create-${step}`)
    if (errors.error) {
      if (errors.message) {
        setError({ message: errors.message })
      } else {
        dispatch(updateDevelopmentalRubricFormError(errors))
      }
    } else {
      onRedirect(parseInt(step) + 1)
    }
  }
  const onSave = async (e) => {
    e.preventDefault()
    toggleLoading(true)
    try {
      let result = await createDevelopmentalRubric(curriculumId, formData)
      if (result.error) {
        setError(result)
      } else {
        onCancel()
      }
    } finally {
      toggleLoading(false)
    }
  }

  useEffect(() => {
    if (isEmpty(formData)) {
      onCancel()
    }
  }, [formData])
  useEffect(() => {
    dispatch(updateDevelopmentalRubricFormField('minEndDate', today))
  }, [])

  return (
    <>
      <Module.BodyHeader className='border-b border-b-2 border-primary flex justify-between'>
        <Stepper color='primary' activeStep={parseInt(step)} className='w-full'>
          {steps.map((step, index) => (
            <Step key={`step-${index}`}>
              <StepLabel>{step}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <div className='flex items-center justify-end gap-4'>
          <div className='min-w-25px'>
            {parseInt(step) > 0 && (
              <IconTooltipButton
                disabled={loading}
                title='Previous'
                icon='chevron-left'
                classNameIcon='text-lg'
                style={{
                  borderRadius: '0.5rem',
                  padding: '0.45rem',
                  color: 'var(--primary)',
                  backgroundColor: '#fff',
                  width: 25,
                  height: 25
                }}
                className='shadow-sm'
                onClick={() => onRedirect(parseInt(step) - 1)}
              />
            )}
          </div>
          <div className='min-w-100px'>
            {parseInt(step) === steps.length - 1 ? (
              permission.created && (
                <ButtonStartIcon
                  disabled={loading}
                  loading={loading}
                  onClick={onSave}
                  startIcon={['fal', 'check']}
                  color='primary'
                  className='w-full'
                >
                  Save
                </ButtonStartIcon>
              )
            ) : (
              <IconTooltipButton
                disabled={loading}
                title='Next'
                icon='chevron-right'
                classNameIcon='text-lg'
                style={{
                  borderRadius: '0.5rem',
                  padding: '0.45rem',
                  color: 'var(--primary)',
                  backgroundColor: '#fff',
                  width: 25,
                  height: 25
                }}
                className='shadow-sm'
                onClick={onNext}
              />
            )}
          </div>
          <AlertSaveButton
            submodule='Developmental rubrics'
            disabled={loading}
            icon={['fal', 'times']}
            title='Cancel'
            color='primary'
            variant='outlined'
            className='shadow-sm w-full'
            onDontSave={onCancel}
          />
        </div>
      </Module.BodyHeader>
      <Module.BodyContainer loading={loading} classNameWrapper={loading ? '' : 'overflow-auto'}>
        {error?.message && (
          <Alert severity='error' className='mb-4' onClose={() => setError(null)}>
            {error?.message}
          </Alert>
        )}
        <Box className='absolute inset-0'>
          {step === '2' && (
            <IconTooltipButton
              disabled={loading}
              title='Preview'
              icon='expand-alt'
              classNameIcon='text-xl'
              style={{
                padding: '0.45rem',
                backgroundColor: 'var(--primary)',
                color: 'var(--primary-foreground)',
                position: 'absolute',
                width: 35,
                height: 35
              }}
              className='shadow-sm  z-10 right-0 top-0'
              onClick={() => dispatch(updateDevelopmentalRubricPreview('create'))}
            />
          )}
          {['1', '2', '3', '4'].includes(step) && <CardsInformation />}
          {step === '0' && <FormCurriculumElements onChangeAlert={onChangeAlert} />}
          {step === '0' && <FormSubject onChangeAlert={onChangeAlert} />}
          {step === '0' && <FormBasicInformation onChangeAlert={onChangeAlert} />}
          {step === '1' && <FormLearningContinuum onChangeAlert={onChangeAlert} />}
          {step === '2' && <FormGranularSteps onChangeAlert={onChangeAlert} type='create' />}
        </Box>
      </Module.BodyContainer>
    </>
  )
}

export default Create
